import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import FixedCard from "../components/FixedCard";
import SEO from "../components/SEO";

const typeColor = { client:"#8A1613", partner:"#007965", collaborator:"#534AB7", supporter:"#B07D1A" };
const typeLabel = { client:"Client", partner:"Partner", collaborator:"Collaborator", supporter:"Supporter" };

export default function Clients() {
  const [clients,   setClients]   = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    Promise.all([api.getClients(), api.getCampaigns()])
      .then(([c, camp]) => { setClients(c); setCampaigns(camp); })
      .catch(() => {});
  }, []);

  const featured  = clients.filter(c => c.featured && c.type === "client");
  const allClients= clients.filter(c => c.type === "client");
  const partners  = clients.filter(c => c.type !== "client");

  return (
    <>
    <SEO
      title="Our Work"
      path="/clients"
      description="Health campaigns reaching young people across South Africa. See how The Health Plug has worked with leading organisations on TB, HIV, diabetes, maternal health and mental health communications."
    />
    <div style={{ paddingTop:"5rem" }}>

      {/* Hero */}
      <section style={{ background:"#0B0B0B", padding:"6rem 2rem 5rem" }}>
        <div className="container">
          <p className="section-label" style={{ color:"#95BB83" }}>Our Clients</p>
          <h1 style={{ fontFamily:"var(--font)", fontWeight:900, fontSize:"clamp(2.8rem,7vw,6rem)", color:"#fff", lineHeight:0.95, letterSpacing:"-0.03em", marginBottom:"1.25rem" }}>
            Our<br /><span style={{ color:"#8A1613" }}>Clients.</span>
          </h1>
          <p style={{ color:"rgba(255,255,255,0.5)", maxWidth:520, fontFamily:"var(--font-body)", fontSize:"1.05rem", lineHeight:1.75 }}>
            We work with companies and organisations that care about health, access, and young people.
          </p>
        </div>
      </section>

      {/* Featured clients */}
      {featured.length > 0 && (
        <section style={{ background:"#F7F1DF", padding:"6rem 2rem" }}>
          <div className="container">
            <p className="section-label" style={{ color:"#8A1613" }}>Featured Clients</p>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:"1.25rem" }}>
              {featured.map(c => (
                <FixedCard
                  key={c.id}
                  height={300}
                  headerHeight={120}
                  accentColor={typeColor[c.type] || "#8A1613"}
                  headerContent={
                    c.logo_url
                      ? <img src={c.logo_url} alt={c.name}
                          style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"contain", padding:"1rem" }} />
                      : <span style={{ fontFamily:"var(--font)", fontWeight:900, fontSize:"2rem", color:"rgba(255,255,255,0.3)", zIndex:1, position:"relative" }}>{c.name}</span>
                  }
                  eyebrow={typeLabel[c.type]}
                  title={c.name}
                  body={c.description || ""}
                  previewLines={3}
                  modalTitle={c.name}
                  modalSubtitle={typeLabel[c.type]}
                  modalExtra={
                    c.website_url
                      ? <a href={c.website_url} target="_blank" rel="noreferrer"
                          style={{ fontFamily:"var(--font)", fontWeight:700, fontSize:"0.85rem", color:"#007965" }}>
                          Visit website →
                        </a>
                      : null
                  }
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Campaign work */}
      {campaigns.length > 0 && (
        <section style={{ background:"#fff", padding:"6rem 2rem" }}>
          <div className="container">
            <p className="section-label" style={{ color:"#8A1613" }}>Featured Work</p>
            <h2 style={{ fontFamily:"var(--font)", fontWeight:900, fontSize:"clamp(1.8rem,4vw,2.8rem)", color:"#0B0B0B", marginBottom:"0.75rem", letterSpacing:"-0.02em" }}>Campaign Stories</h2>
            <p style={{ color:"rgba(11,11,11,0.5)", fontFamily:"var(--font-body)", marginBottom:"2.5rem" }}>A look at health communication projects, campaigns and content created with communities and partners.</p>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:"1.25rem" }}>
              {campaigns.map(c => (
                <FixedCard
                  key={c.id}
                  height={300}
                  headerHeight={130}
                  accentColor={c.color || "#8A1613"}
                  headerContent={
                    c.image_url
                      ? <img src={c.image_url} alt={c.title}
                          style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover" }} />
                      : <span style={{ fontFamily:"var(--font)", fontSize:"3rem", fontWeight:900, color:"rgba(255,255,255,0.18)", zIndex:1, position:"relative" }}>{c.tag}</span>
                  }
                  eyebrow={`${c.category} · ${c.year}`}
                  title={c.title}
                  meta={c.client}
                  body={c.description}
                  previewLines={2}
                  modalTitle={c.title}
                  modalSubtitle={`${c.category} · ${c.year}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Logo wall */}
      <section style={{ background:"#0B0B0B", padding:"5rem 2rem" }}>
        <div className="container">
          <p style={{ textAlign:"center", fontFamily:"var(--font)", fontWeight:700, fontSize:"0.75rem", letterSpacing:"0.15em", textTransform:"uppercase", color:"rgba(255,255,255,0.3)", marginBottom:"2rem" }}>All Clients</p>
          <div style={{ display:"flex", gap:"1.25rem", flexWrap:"wrap", justifyContent:"center", marginBottom:"3rem" }}>
            {allClients.map(c => (
              <div key={c.id} style={{ background:"rgba(255,255,255,0.05)", borderRadius:14, padding:"1.25rem 2rem", border:"1px solid rgba(255,255,255,0.08)", display:"flex", alignItems:"center", justifyContent:"center", minWidth:120 }}>
                {c.logo_url
                  ? <img src={c.logo_url} alt={c.name} style={{ maxHeight:44, maxWidth:120, objectFit:"contain", filter:"brightness(0) invert(1)", opacity:0.7 }} />
                  : <span style={{ fontFamily:"var(--font)", fontWeight:700, color:"rgba(255,255,255,0.5)", fontSize:"0.95rem" }}>{c.name}</span>
                }
              </div>
            ))}
          </div>

          {partners.length > 0 && (
            <>
              <p style={{ textAlign:"center", fontFamily:"var(--font)", fontWeight:700, fontSize:"0.75rem", letterSpacing:"0.15em", textTransform:"uppercase", color:"rgba(255,255,255,0.3)", marginBottom:"1.5rem" }}>Partners, Collaborators & Supporters</p>
              <div style={{ display:"flex", gap:"1rem", flexWrap:"wrap", justifyContent:"center" }}>
                {partners.map(p => (
                  <div key={p.id} style={{ background:"rgba(255,255,255,0.04)", borderRadius:12, padding:"0.9rem 1.5rem", border:`1px solid ${typeColor[p.type]||"#888"}33` }}>
                    <span style={{ fontFamily:"var(--font)", fontWeight:600, color:"rgba(255,255,255,0.5)", fontSize:"0.88rem" }}>{p.name}</span>
                    <span style={{ marginLeft:"0.5rem", fontSize:"0.65rem", fontWeight:700, color:typeColor[p.type], textTransform:"uppercase", letterSpacing:"0.08em" }}>{typeLabel[p.type]}</span>
                  </div>
                ))}
              </div>
              <p style={{ color:"rgba(255,255,255,0.25)", fontSize:"0.75rem", textAlign:"center", marginTop:"1rem", fontStyle:"italic" }}>* Partners, collaborators and supporters are not necessarily paying clients.</p>
            </>
          )}
        </div>
      </section>

      <section style={{ background:"#8A1613", padding:"5rem 2rem", textAlign:"center" }}>
        <div className="container-sm">
          <h2 style={{ fontFamily:"var(--font)", fontWeight:900, fontSize:"clamp(1.8rem,4vw,3rem)", color:"#fff", marginBottom:"1rem", letterSpacing:"-0.02em" }}>Let's Operate</h2>
          <p style={{ color:"rgba(255,255,255,0.65)", fontFamily:"var(--font-body)", marginBottom:"2rem" }}>Let's build a health campaign that actually reaches young people.</p>
          <Link to="/contact" className="btn-outline-white">Plan a Health Campaign</Link>
        </div>
      </section>

    </div>
    </>
  );
}
